<template>
  <v-card outlined class="rounded-lg mb-4 objection-panel">
    <div class="objection-panel__header">
      <div>
        <div class="text-overline grey--text text--darken-1 objection-panel__overline">Pedido da Recuperanda</div>
        <div class="text-subtitle-1 font-weight-bold">{{ showCreditor && event.user ? event.user.name : 'Revisão do seu crédito' }}</div>
        <div class="text-caption grey--text text--darken-1">
          <template v-if="showCreditor && event.user">{{ registration }} · </template>Apresentado em {{ $moment(event.created_at).utc().format('DD/MM/YYYY') }}
        </div>
      </div>
      <v-spacer />
      <v-chip small label :color="status.color" text-color="white">{{ status.label }}</v-chip>
    </div>
    <v-divider />

    <div class="pa-4">
      <div v-for="(item, index) in event.items || []" :key="item.id || index" :class="{ 'objection-panel__item': index > 0 }">
        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <div class="objection-field__label">Valor proposto</div>
            <div class="objection-field__value objection-field__value--amount">{{ money(item.amount, item.currency) }}</div>
          </v-col>
          <v-col v-if="item.type" cols="12" sm="6" md="4">
            <div class="objection-field__label">Classe</div>
            <div class="objection-field__value">{{ classLabel(item.type) }}</div>
          </v-col>
          <v-col v-if="item.origin" cols="12" sm="6" md="4">
            <div class="objection-field__label">Origem</div>
            <div class="objection-field__value">{{ item.origin }}</div>
          </v-col>
          <v-col v-if="item.observation" cols="12">
            <div class="objection-field__label">Justificativa da Recuperanda</div>
            <div class="objection-field__text">{{ item.observation }}</div>
          </v-col>
        </v-row>
      </div>

      <div v-if="Number(event.status) !== 0 && analysis" class="objection-panel__note mt-3">
        <div class="objection-field__label">Análise da Administração Judicial</div>
        <div class="objection-field__text">{{ analysis }}</div>
      </div>

      <!-- Resposta do credor (contraditório) ao pedido da Recuperanda. -->
      <div v-if="answer" class="creditor-answer mt-4" :class="{ 'creditor-answer--pending': answer.can_answer && !answer.answered }">
        <template v-if="answer.answered">
          <div class="d-flex align-center flex-wrap">
            <v-icon small color="primary" class="mr-2">mdi-message-reply-text-outline</v-icon>
            <span class="text-subtitle-2 font-weight-bold mr-3">{{ showCreditor ? 'Resposta do credor' : 'Sua resposta' }}</span>
            <v-chip x-small label :color="answerColor" text-color="white">{{ answer.status_label }}</v-chip>
            <v-spacer />
            <v-btn v-if="canEdit" small text color="primary" @click="dialog = true"><v-icon left small>mdi-pencil</v-icon>Alterar resposta</v-btn>
          </div>
          <v-row dense class="mt-1">
            <v-col cols="12" sm="6" md="4">
              <div class="objection-field__label">Respondido em</div>
              <div class="objection-field__value">{{ $moment(answer.answered_at).format('DD/MM/YYYY') }}{{ answer.answered_by === 'lawyer' ? ' (procurador)' : '' }}</div>
            </v-col>
            <v-col v-if="answer.amount" cols="12" sm="6" md="4">
              <div class="objection-field__label">Valor com o qual concorda</div>
              <div class="objection-field__value objection-field__value--amount">{{ money(answer.amount, 1) }}</div>
            </v-col>
            <v-col cols="12">
              <div class="objection-field__label">Justificativa</div>
              <div class="objection-field__text">{{ answer.reason }}</div>
            </v-col>
            <v-col v-if="answer.docs.length" cols="12">
              <div class="objection-field__label">Documentos</div>
              <v-btn v-for="doc in answer.docs" :key="doc.id" :href="doc.url" :disabled="!doc.url" target="_blank" rel="noopener noreferrer"
                small outlined color="primary" class="mr-2 mt-1"><v-icon left small>mdi-file-document-outline</v-icon>{{ doc.name }}</v-btn>
            </v-col>
          </v-row>
          <div v-if="answer.can_answer" class="text-caption grey--text mt-1">Pode ser alterada até {{ $moment(answer.deadline_at).format('DD/MM/YYYY') }} ou até a análise da Administração Judicial.</div>
        </template>

        <template v-else-if="answer.can_answer">
          <div class="d-flex align-center flex-wrap">
            <div class="mr-4">
              <div class="text-subtitle-2 font-weight-bold">{{ showCreditor ? 'O credor pode responder a este pedido' : 'Você pode responder a este pedido' }}</div>
              <div class="text-body-2">Prazo para resposta até <strong>{{ $moment(answer.deadline_at).format('DD/MM/YYYY') }}</strong>.</div>
            </div>
            <v-spacer />
            <v-btn v-if="moduleOpen" color="primary" elevation="0" class="mt-2 mt-sm-0" @click="dialog = true">
              <v-icon left small>mdi-message-reply-text-outline</v-icon>Responder
            </v-btn>
          </div>
          <div v-if="!moduleOpen" class="text-body-2 mt-2">{{ $modules.message('site.review.contradictory') }}</div>
        </template>

        <div v-else class="text-body-2 grey--text text--darken-1">
          <template v-if="answer.expired">O prazo para responder terminou em {{ $moment(answer.deadline_at).format('DD/MM/YYYY') }}, sem resposta.</template>
          <template v-else>Este pedido já foi analisado pela Administração Judicial.</template>
        </div>
      </div>
    </div>

    <CreditorAnswerDialog v-if="answer" v-model="dialog" :event="event" :lawyer="showCreditor" @saved="onSaved" />
  </v-card>
</template>

<script>
import creditorTypes from '@/common/data/creditor-types.data';
import { plainText, formatMoney } from '@/common/util/objection-phase';
import { eventHub } from '@/main';
import CreditorAnswerDialog from './CreditorAnswerDialog.vue';

const STATUS = {
  0: { label: 'Em análise pela Administração Judicial', color: 'blue-grey' },
  1: { label: 'Aprovado', color: 'green darken-1' },
  2: { label: 'Rejeitado', color: 'red darken-1' },
  3: { label: 'Aprovado parcialmente', color: 'indigo' },
  4: { label: 'Documentos solicitados', color: 'orange darken-2' },
  5: { label: 'Documentos enviados', color: 'orange darken-2' },
};

// Pedido de revisão apresentado pela Recuperanda na Fase 2 (somente leitura, para credor e procurador).
export default {
  name: 'DebtorRequestCard',
  components: { CreditorAnswerDialog },
  props: {
    event: { type: Object, required: true },
    showCreditor: { type: Boolean, default: false },
  },
  data: () => ({ dialog: false }),
  computed: {
    answer() { return this.event.creditor_answer || null; },
    moduleOpen() { return this.$modules.isOpen('site.review.contradictory'); },
    canEdit() { return !!this.answer && this.answer.can_answer && this.moduleOpen; },
    answerColor() { return { 1: 'green darken-1', 2: 'red darken-1', 3: 'indigo' }[Number(this.answer && this.answer.status)] || 'blue-grey'; },
    status() { return STATUS[Number(this.event.status)] || STATUS[0]; },
    analysis() { return plainText(this.event.reason); },
    registration() {
      const digits = String((this.event.user && this.event.user.registration) || '').replace(/\D/g, '');
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      return digits;
    },
  },
  methods: {
    classLabel(type) {
      const found = creditorTypes.find(item => item.id === Number(type));
      return found ? found.name : '—';
    },
    money(value, currency) { return formatMoney(value, currency); },
    onSaved(answer) {
      this.$set(this.event, 'creditor_answer', answer);
      this.$dialog.notify.success('Resposta enviada.', { position: 'top-right', timeout: 3000 });
      eventHub.$emit('DEBTOR-REQUESTS-REFRESH');
      this.$emit('answered', answer);
    },
  },
};
</script>

<style>
.objection-panel__header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 16px; background: #f5f5f5; border-radius: 8px 8px 0 0; }
.objection-panel__overline { line-height: 1.4 !important; }
.objection-panel__item { border-top: 1px solid rgba(0, 0, 0, .08); margin-top: 12px; padding-top: 12px; }
.creditor-answer { border: 1px solid #e3e8f0; background: #f7f9fc; border-radius: 8px; padding: 12px 14px; }
.creditor-answer--pending { border-color: #ffb74d; background: #fff8e1; }
.objection-panel__note { background: #fafafa; border-radius: 6px; padding: 10px 12px; }
.objection-field__label { font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #757575; margin-bottom: 2px; }
.objection-field__value { font-size: 14px; color: #212121; }
.objection-field__value--amount { font-size: 16px; font-weight: 700; color: #1a237e; }
.objection-field__text { font-size: 14px; color: #424242; white-space: pre-line; }
</style>
